import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
  Hash,
  X,
  AlertTriangle,
  Download,
  Info,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw
} from 'lucide-react';
import { tenantAPI, Zone, CreateZoneData, UpdateZoneData } from '@/services/api/tenants';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import {
  useZonesPaginated,
  useZoneMutations,
  useRefreshZoneRecallCounts,
  useCurrentRecallCounts,
  zoneKeys
} from '../hooks/useZones';
import writeXlsxFile, { type Column } from 'write-excel-file/browser';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getZoneColor, getZoneSizeLabel, getRecallCountFromRPC } from '../utils/zoneStatistics';
import { SystemRole } from '@/types';
import { KNOWN_OEMS } from '@/constants/oems';

type ZoneExportRow = {
  zoneName: string;
  region: string;
  zipCodes: string;
};

type ZoneZipCodeExportRow = {
  zoneName: string;
  zipCodes: string;
};

const zoneExportColumns: Column<ZoneExportRow>[] = [
  { width: 24, header: 'Zone Name', cell: row => row.zoneName },
  { width: 18, header: 'Region', cell: row => row.region },
  { width: 60, header: 'Zip Codes', cell: row => row.zipCodes }
];

const zoneZipCodeExportColumns: Column<ZoneZipCodeExportRow>[] = [
  { width: 24, header: 'Zone Name', cell: row => row.zoneName },
  { width: 80, header: 'Zip Codes', cell: row => row.zipCodes }
];

// Manual Zone Management Component (existing functionality)
// Uses React Query for caching and performance (same pattern as useCampaigns and useDealers)
const ManualZoneTab: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { session, hasSystemRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [oemFilter, setOemFilter] = useState<string>('all'); // NEW: OEM filter
  const [selectedZones, setSelectedZones] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [loadingZipCodes, setLoadingZipCodes] = useState<Set<string>>(new Set());

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const ZONES_PER_PAGE = pageSize;

  // Use React Query hook for zones (with caching - 10 min staleTime)
  // Note: includeUserNames=false for faster initial load (user names loaded lazily on tooltip hover)
  const {
    data: zonesData,
    isLoading: loading,
    error: zonesError,
    isFetching: loadingMore
  } = useZonesPaginated(
    page,
    ZONES_PER_PAGE,
    searchTerm || undefined,
    statusFilter,
    regionFilter !== 'all' ? regionFilter : undefined,
    oemFilter !== 'all' ? oemFilter : undefined, // NEW: Pass OEM filter
    false // Don't fetch user names on initial load for performance
  );

  // Reset to page 1 when filters or page size change (including OEM filter)
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, regionFilter, oemFilter, pageSize]);

  // Use zones from current page only (no accumulation for pagination)
  const zones = zonesData?.zones || [];
  const totalZones = zonesData?.totalCount || 0;
  const totalPages = Math.ceil(totalZones / ZONES_PER_PAGE);
  const hasMore = zonesData?.hasMore || false;

  const zoneIds = zones.map(z => z.id);

  // Deliberately NOT tenant-scoped. A zone's size is a property of the territory, not of
  // whoever is viewing it — passing the session tenant made the same zone read 342 or 702
  // depending on the tenant switcher. The RPC applies the as-of cutoff server-side.
  const { data: currentRecallCounts } = useCurrentRecallCounts(zoneIds);

  // Current recall count for a zone: live RPC value, falling back to the denormalized
  // column that refresh_zone_recall_counts_v2() keeps in sync with the same rule.
  const getRecallCount = (zone: Zone): number | null => {
    if (currentRecallCounts && zone.id in currentRecallCounts) {
      return currentRecallCounts[zone.id] ?? null;
    }
    return zone.total_recalls_current ?? null;
  };

  // Size label reflects the zone as it is NOW, not as it was drawn.
  const getCurrentSizeLabel = (zone: Zone): string | null => {
    const count = getRecallCount(zone);
    return count == null ? null : getZoneSizeLabel(count);
  };

  // Only surfaced when the size category actually moved since creation.
  const getCreationSizeLabel = (zone: Zone): string | null => {
    if (zone.total_recalls_at_creation == null) return null;
    const creationLabel = getZoneSizeLabel(zone.total_recalls_at_creation);
    return creationLabel === getCurrentSizeLabel(zone) ? null : creationLabel;
  };

  // Calculate pagination info
  const startIndex = (page - 1) * ZONES_PER_PAGE;
  const endIndex = Math.min(startIndex + zones.length, totalZones);

  // Zone mutations with React Query (handles cache updates automatically)
  const {
    createZone: createZoneMutation,
    updateZone: updateZoneMutation,
    deleteZone: deleteZoneMutation
  } = useZoneMutations();
  const refreshRecallsMutation = useRefreshZoneRecallCounts();
  const isSuperAdmin = hasSystemRole(SystemRole.SUPER_ADMIN);

  // Load available regions with React Query caching
  const { data: availableRegions = [] } = useQuery({
    queryKey: ['zones', 'regions'],
    queryFn: () => tenantAPI.getUniqueRegions(),
    staleTime: 15 * 60 * 1000, // 15 minutes - regions change infrequently
    gcTime: 30 * 60 * 1000
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [originalZipCodes, setOriginalZipCodes] = useState<string[]>([]); // Store original zip codes for comparison
  const [formLoading, setFormLoading] = useState(false);
  const [newZipCode, setNewZipCode] = useState('');
  const [zipCodeProgress, setZipCodeProgress] = useState<string | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    zone_name: string;
    zone_slug: string;
    description: string;
    region: string;
    country: string;
    status: string;
    oem: string;
    zip_codes: string[];
  }>({
    zone_name: '',
    zone_slug: '',
    description: '',
    region: '',
    country: 'US',
    status: 'active',
    oem: '',
    zip_codes: []
  });

  // Preview recall count state
  const [previewRecallCount, setPreviewRecallCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Calculate preview recall count when OEM or ZIP codes change
  useEffect(() => {
    // Only calculate if we have both OEM and ZIP codes
    if (!formData.oem || formData.zip_codes.length === 0) {
      setPreviewRecallCount(null);
      return;
    }

    // Debounce the calculation
    const timeoutId = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const result = await getRecallCountFromRPC(formData.zip_codes, formData.oem);
        setPreviewRecallCount(result?.totalRecalls ?? 0);
      } catch (error) {
        console.error('[ZoneSetupTab] Failed to calculate preview recall count:', error);
        setPreviewRecallCount(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData.oem, formData.zip_codes]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(prev => prev + 1);
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(parseInt(newSize));
    setPage(1);
  };

  const handleRefreshRecalls = async () => {
    toast({
      title: 'Refreshing recall counts',
      description: 'This may take a minute. You can keep working.'
    });

    try {
      await refreshRecallsMutation.mutateAsync();
      toast({
        title: 'Recall counts updated',
        description: 'Recalls (Current) has been refreshed.'
      });
    } catch (error) {
      toast({
        title: 'Refresh failed',
        description: error instanceof Error ? error.message : 'Unable to refresh recall counts.',
        variant: 'destructive'
      });
    }
  };

  const openCreateDialog = () => {
    setEditingZone(null);
    setOriginalZipCodes([]);
    setFormData({
      zone_name: '',
      zone_slug: '',
      description: '',
      region: '',
      country: 'US',
      status: 'active',
      oem: '',
      zip_codes: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = async (zone: Zone) => {
    setEditingZone(zone);

    // Load zip codes for this zone using React Query fetchQuery
    // This will use cached data if available, or fetch and cache if not
    let zipCodes: string[] = [];
    try {
      // eslint-disable-next-line no-console
      console.log(
        `[openEditDialog] Loading zip codes for zone: ${zone.zone_name} (ID: ${zone.id})`
      );

      // Use fetchQuery to get zip codes with proper React Query caching
      // This will check cache first, then fetch if needed, and cache the result
      zipCodes = await queryClient.fetchQuery({
        queryKey: zoneKeys.zipCodes(zone.id),
        queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
        staleTime: 15 * 60 * 1000 // 15 minutes - same as useZoneZipCodes hook
      });

      // eslint-disable-next-line no-console
      console.log(
        `[openEditDialog] Successfully loaded ${zipCodes.length} zip codes for zone ${zone.zone_name}`
      );
    } catch (error) {
      // Log full error details for debugging
      // eslint-disable-next-line no-console
      console.error('[openEditDialog] Error loading zip codes:', {
        error,
        zoneId: zone.id,
        zoneName: zone.zone_name,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });

      // Show specific error messages based on error type
      let errorTitle = 'Warning';
      let errorDescription =
        'Failed to load zip codes for this zone. You can still view and edit other zone properties.';

      if (error instanceof Error) {
        if (
          error.message.includes('RPC function not found') ||
          error.message.includes('Could not find the function')
        ) {
          errorTitle = 'Database Migration Required';
          errorDescription =
            'The database function for reading zone zip codes is not available. ' +
            'Please run: migrations/add-get-zone-zip-codes-rpc-function.sql';
        } else if (
          error.message.includes('Access denied') ||
          error.message.includes('RLS') ||
          error.message.includes('permission')
        ) {
          errorTitle = 'Access Denied';
          errorDescription =
            'Unable to load zip codes due to access restrictions. ' +
            'Please check your permissions or contact an administrator.';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorTitle = 'Network Error';
          errorDescription =
            'Failed to load zip codes due to a network issue. ' +
            'Please check your connection and try again.';
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: 'destructive'
      });

      // Continue with empty array - user can still edit other fields
      zipCodes = [];
    }

    // Store original zip codes for comparison during update
    setOriginalZipCodes([...zipCodes]);

    setFormData({
      zone_name: zone.zone_name || '',
      zone_slug: zone.zone_slug || '',
      description: zone.description || '',
      region: zone.region || '',
      country: zone.country || 'US',
      status: zone.status || 'active',
      oem: zone.oem || '',
      zip_codes: zipCodes
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.zone_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Zone name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.oem) {
      toast({
        title: 'Validation Error',
        description: 'OEM is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      setFormLoading(true);
      setZipCodeProgress(null);

      if (editingZone) {
        // Compare current formData zip codes with original zip codes from when dialog opened
        const currentZipCodes = formData.zip_codes || [];
        const zipCodesChanged =
          originalZipCodes.length !== currentZipCodes.length ||
          !originalZipCodes.every(zip => currentZipCodes.includes(zip)) ||
          !currentZipCodes.every(zip => originalZipCodes.includes(zip));

        const updateData: UpdateZoneData = {
          zone_name: formData.zone_name.trim(),
          status: formData.status,
          oem: formData.oem,
          ...(formData.zone_slug.trim() ? { zone_slug: formData.zone_slug.trim() } : {}),
          ...(formData.description ? { description: formData.description } : {}),
          ...(formData.region ? { region: formData.region } : {}),
          ...(formData.country ? { country: formData.country } : {}),
          ...(zipCodesChanged ? { zip_codes: formData.zip_codes } : {})
        };

        // Show progress for large ZIP code updates
        if (zipCodesChanged && formData.zip_codes.length > 50) {
          setZipCodeProgress(`Updating ${formData.zip_codes.length} ZIP codes...`);
        }

        // Use React Query mutation (handles cache updates automatically)
        await updateZoneMutation.mutateAsync({
          id: editingZone.id,
          data: updateData,
          ...(session?.user?.id ? { userId: session.user.id } : {})
        });

        toast({
          title: 'Zone Updated',
          description: `Zone "${formData.zone_name}" has been updated successfully`
        });
      } else {
        const createData: CreateZoneData = {
          zone_name: formData.zone_name.trim(),
          status: formData.status,
          oem: formData.oem,
          zip_codes: formData.zip_codes,
          ...(formData.zone_slug.trim() ? { zone_slug: formData.zone_slug.trim() } : {}),
          ...(formData.description ? { description: formData.description } : {}),
          ...(formData.region ? { region: formData.region } : {}),
          ...(formData.country ? { country: formData.country } : {})
        };

        // Show progress for large ZIP code operations
        if (formData.zip_codes.length > 50) {
          setZipCodeProgress(`Creating zone with ${formData.zip_codes.length} ZIP codes...`);
        }

        // Use React Query mutation (handles cache updates automatically)
        await createZoneMutation.mutateAsync({
          data: createData,
          ...(session?.user?.id ? { userId: session.user.id } : {})
        });

        toast({
          title: 'Zone Created',
          description: `Zone "${formData.zone_name}" has been created successfully`
        });
      }

      setDialogOpen(false);
      setZipCodeProgress(null);

      // Invalidate regions query in case a new region was added
      if (formData.region && !availableRegions.includes(formData.region)) {
        queryClient.invalidateQueries({ queryKey: ['zones', 'regions'] });
      }

      // Reset to page 1 if creating a new zone
      if (!editingZone) {
        setPage(1);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || `Failed to ${editingZone ? 'update' : 'create'} zone`,
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = (zone: Zone) => {
    setZoneToDelete(zone);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!zoneToDelete) return;

    try {
      setDeleting(true);

      // Use React Query mutation (handles cache updates automatically)
      await deleteZoneMutation.mutateAsync({
        id: zoneToDelete.id,
        hard: true,
        ...(session?.user?.id ? { userId: session.user.id } : {})
      });

      toast({
        title: 'Zone Deleted',
        description: `Zone "${zoneToDelete.zone_name}" has been deleted permanently`
      });

      setDeleteDialogOpen(false);
      setZoneToDelete(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete zone',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setZoneToDelete(null);
  };

  const addZipCode = () => {
    const zip = newZipCode.trim();
    if (zip && !formData.zip_codes.includes(zip)) {
      setFormData(prev => ({
        ...prev,
        zip_codes: [...prev.zip_codes, zip]
      }));
      setNewZipCode('');
    }
  };

  const removeZipCode = (zipCode: string) => {
    setFormData(prev => ({
      ...prev,
      zip_codes: prev.zip_codes.filter(z => z !== zipCode)
    }));
  };

  const handleZipCodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addZipCode();
    }
  };

  // Note: Filtering is now done server-side via the API
  // The zones returned are already filtered based on searchTerm, statusFilter, and regionFilter

  // Toggle zone selection
  const toggleZoneSelection = (zoneId: string) => {
    setSelectedZones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) {
        newSet.delete(zoneId);
      } else {
        newSet.add(zoneId);
      }
      return newSet;
    });
  };

  // Toggle select all (for current page only)
  const toggleSelectAll = () => {
    const currentPageZoneIds = new Set(zones.map(z => z.id));
    const allSelected = zones.length > 0 && zones.every(z => selectedZones.has(z.id));

    if (allSelected) {
      // Deselect all zones on current page
      setSelectedZones(prev => {
        const newSet = new Set(prev);
        currentPageZoneIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all zones on current page
      setSelectedZones(prev => {
        const newSet = new Set(prev);
        currentPageZoneIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  // Export selected zones to Excel
  const handleExportZones = async () => {
    if (selectedZones.size === 0 || exporting) {
      if (selectedZones.size === 0) {
        toast({
          title: 'No zones selected',
          description: 'Please select at least one zone to export',
          variant: 'destructive'
        });
      }
      return;
    }

    try {
      setExporting(true);

      // Get selected zone objects from all zones (not just filtered)
      const zonesToExport = zones.filter(z => selectedZones.has(z.id));

      // Fetch zip codes for each selected zone
      const exportData = await Promise.all(
        zonesToExport.map(async zone => {
          const zipCodes = await tenantAPI.getZoneZipCodes(zone.id);

          return {
            zoneName: zone.zone_name || '',
            region: zone.region || '',
            zipCodes: zipCodes.join(', ')
          };
        })
      );

      const fileName = `zones-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      const zoneExportWorkbook = writeXlsxFile(exportData, {
        columns: zoneExportColumns,
        sheet: 'Zones'
      });
      await zoneExportWorkbook.toFile(fileName);

      toast({
        title: 'Export successful',
        description: `Exported ${zonesToExport.length} zone(s) to ${fileName}`
      });

      // Clear selection after export
      setSelectedZones(new Set());
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export zones',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };

  // Export zip codes for a single zone
  const handleExportSingleZone = async (zone: Zone) => {
    setLoadingZipCodes(prev => new Set(prev).add(zone.id));
    try {
      const zipCodes = await queryClient.fetchQuery({
        queryKey: zoneKeys.zipCodes(zone.id),
        queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
        staleTime: 15 * 60 * 1000
      });

      const exportData: ZoneZipCodeExportRow[] = [
        { zoneName: zone.zone_name || '', zipCodes: zipCodes.join(', ') }
      ];
      const zipCodeWorkbook = writeXlsxFile(exportData, {
        columns: zoneZipCodeExportColumns,
        sheet: 'Zip Codes'
      });
      await zipCodeWorkbook.toFile(`${zone.zone_slug || zone.zone_name}-zip-codes.xlsx`);

      toast({ title: 'Export successful', description: `Exported ${zipCodes.length} zip codes` });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export zip codes',
        variant: 'destructive'
      });
    } finally {
      setLoadingZipCodes(prev => {
        const next = new Set(prev);
        next.delete(zone.id);
        return next;
      });
    }
  };

  // Copy zip codes to clipboard for a single zone
  const handleCopyZipCodes = async (zone: Zone) => {
    setLoadingZipCodes(prev => new Set(prev).add(zone.id));
    try {
      const zipCodes = await queryClient.fetchQuery({
        queryKey: zoneKeys.zipCodes(zone.id),
        queryFn: () => tenantAPI.getZoneZipCodes(zone.id),
        staleTime: 15 * 60 * 1000
      });

      await navigator.clipboard.writeText(zipCodes.join(', '));
      toast({ title: 'Copied to clipboard', description: `${zipCodes.length} zip codes copied` });
    } catch (error: any) {
      toast({
        title: 'Copy failed',
        description: error.message || 'Failed to copy zip codes',
        variant: 'destructive'
      });
    } finally {
      setLoadingZipCodes(prev => {
        const next = new Set(prev);
        next.delete(zone.id);
        return next;
      });
    }
  };

  // Show error if zones query failed
  if (zonesError) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-8 w-8 mx-auto mb-2 text-destructive' />
          <p className='text-destructive'>
            {zonesError instanceof Error ? zonesError.message : 'Failed to load zones'}
          </p>
        </div>
      </div>
    );
  }

  if (loading && zones.length === 0) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header Actions */}
      <div className='flex items-center justify-end'>
        <div className='flex items-center gap-2'>
          <Button
            onClick={handleExportZones}
            disabled={exporting || selectedZones.size === 0}
            variant='outline'
          >
            {exporting ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Exporting...
              </>
            ) : (
              <>
                <Download className='h-4 w-4 mr-2' />
                {selectedZones.size > 0
                  ? `Export Selected (${selectedZones.size})`
                  : 'Export Zones'}
              </>
            )}
          </Button>
          <Button onClick={openCreateDialog}>
            <Plus className='h-4 w-4 mr-2' />
            Add Zone
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-2'>
              <Label>Search</Label>
              <div className='relative'>
                <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='Search zones...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className='pl-8'
                />
              </div>
            </div>
            <div className='space-y-2'>
              <Label>OEM</Label>
              <Select value={oemFilter} onValueChange={setOemFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='All OEMs' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All OEMs</SelectItem>
                  {KNOWN_OEMS.filter(oem => oem !== 'Other').map(oem => (
                    <SelectItem key={oem} value={oem}>
                      {oem}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label>Region</Label>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='All Regions' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Regions</SelectItem>
                  {availableRegions.map(region => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={v => setStatusFilter(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Statuses</SelectItem>
                  <SelectItem value='active'>Active</SelectItem>
                  <SelectItem value='inactive'>Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zones Table */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>Zones</CardTitle>
              <CardDescription>
                Manage zone configurations and zip code associations
              </CardDescription>
            </div>
            <div className='flex items-center gap-2'>
              {isSuperAdmin && (
                <Button
                  variant='outline'
                  size='sm'
                  className='gap-2'
                  onClick={handleRefreshRecalls}
                  disabled={refreshRecallsMutation.isPending}
                >
                  {refreshRecallsMutation.isPending ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='h-4 w-4' />
                  )}
                  Refresh Recalls
                </Button>
              )}
              <Label className='text-sm text-muted-foreground whitespace-nowrap'>Show:</Label>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className='w-20'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='25'>25</SelectItem>
                  <SelectItem value='50'>50</SelectItem>
                  <SelectItem value='100'>100</SelectItem>
                  <SelectItem value='200'>200</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {zones.length === 0 && !loading ? (
            <div className='text-center py-12 text-muted-foreground'>
              <MapPin className='h-12 w-12 mx-auto mb-4 opacity-50' />
              <p>No zones found</p>
              {totalZones > 0 && <p className='text-sm mt-2'>Try adjusting your filters</p>}
            </div>
          ) : (
            <>
              <div className='overflow-x-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-12'>
                        <Checkbox
                          checked={zones.length > 0 && zones.every(z => selectedZones.has(z.id))}
                          onCheckedChange={toggleSelectAll}
                          aria-label='Select all zones'
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>OEM</TableHead>
                      <TableHead>Zone Size</TableHead>
                      <TableHead>Recalls (Creation)</TableHead>
                      <TableHead>Recalls (Current)</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className='w-10'>Info</TableHead>
                      <TableHead className='w-10'>Export</TableHead>
                      <TableHead className='w-10'>Copy</TableHead>
                      <TableHead className='w-10'>Edit</TableHead>
                      <TableHead className='w-10'>Delete</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zones.map(zone => (
                      <TableRow key={zone.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedZones.has(zone.id)}
                            onCheckedChange={() => toggleZoneSelection(zone.id)}
                            aria-label={`Select ${zone.zone_name}`}
                          />
                        </TableCell>
                        <TableCell className='font-medium'>{zone.zone_name}</TableCell>
                        <TableCell>
                          <Badge variant='outline'>{zone.oem}</Badge>
                        </TableCell>
                        <TableCell>
                          {getRecallCount(zone) != null ? (
                            <div className='flex flex-col gap-0.5'>
                              <Badge
                                className='text-white border-0 w-fit'
                                style={{
                                  backgroundColor: getZoneColor(getRecallCount(zone)!)
                                }}
                              >
                                {getCurrentSizeLabel(zone)}
                              </Badge>
                              {getCreationSizeLabel(zone) && (
                                <span className='text-[10px] text-muted-foreground'>
                                  was {getCreationSizeLabel(zone)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell className='font-medium'>
                          {zone.total_recalls_at_creation ?? '—'}
                        </TableCell>
                        <TableCell>
                          <div className='flex flex-col gap-0.5'>
                            <span
                              className={`font-medium ${
                                getRecallCount(zone) != null
                                  ? getRecallCount(zone)! > (zone.total_recalls_at_creation || 0)
                                    ? 'text-red-600'
                                    : 'text-green-600'
                                  : ''
                              }`}
                            >
                              {getRecallCount(zone) ?? '—'}
                            </span>
                            {zone.latest_snapshot_date && (
                              <span
                                className='text-[10px] text-muted-foreground'
                                title='Most recent crawl this count is based on'
                              >
                                as of {zone.latest_snapshot_date}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className='text-xs text-muted-foreground'>
                          {zone.created_at ? new Date(zone.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell className='text-xs text-muted-foreground'>
                          {zone.updated_at ? new Date(zone.updated_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          {zone.status === 'active' ? (
                            <Badge variant='default' className='bg-green-500'>
                              Active
                            </Badge>
                          ) : (
                            <Badge variant='secondary'>Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                                <Info className='h-4 w-4' />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent side='left' className='w-64'>
                              <div className='space-y-3 text-sm'>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Slug</span>
                                  <code className='text-xs bg-muted px-2 py-0.5 rounded'>
                                    {zone.zone_slug || 'N/A'}
                                  </code>
                                </div>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Region</span>
                                  <span className='font-medium'>{zone.region || 'N/A'}</span>
                                </div>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Country</span>
                                  <span className='font-medium'>{zone.country || 'US'}</span>
                                </div>
                                <div className='flex items-center justify-between'>
                                  <span className='text-muted-foreground'>Zip Codes</span>
                                  <Badge variant='outline' className='text-xs'>
                                    <Hash className='h-3 w-3 mr-1' />
                                    {zone.zip_code_count || 0}
                                  </Badge>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={() => handleExportSingleZone(zone)}
                            disabled={loadingZipCodes.has(zone.id)}
                            title='Export zip codes'
                          >
                            {loadingZipCodes.has(zone.id) ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              <Download className='h-4 w-4' />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={() => handleCopyZipCodes(zone)}
                            disabled={loadingZipCodes.has(zone.id)}
                            title='Copy zip codes to clipboard'
                          >
                            {loadingZipCodes.has(zone.id) ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              <Copy className='h-4 w-4' />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-8 w-8 p-0'
                            onClick={() => openEditDialog(zone)}
                          >
                            <Edit className='h-4 w-4' />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDelete(zone)}
                            className='text-destructive hover:text-destructive h-8 w-8 p-0'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalZones > 0 && (
                <div className='flex items-center justify-between border-t pt-4 mt-4'>
                  <div className='text-sm text-muted-foreground'>
                    Showing <span className='font-medium text-foreground'>{startIndex + 1}</span> to{' '}
                    <span className='font-medium text-foreground'>{endIndex}</span> of{' '}
                    <span className='font-medium text-foreground'>{totalZones}</span> zones
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handlePreviousPage}
                      disabled={page <= 1 || loading}
                    >
                      <ChevronLeft className='h-4 w-4 mr-1' />
                      Previous
                    </Button>
                    <div className='flex items-center gap-1'>
                      <span className='text-sm text-muted-foreground'>Page</span>
                      <span className='text-sm font-medium text-foreground'>{page}</span>
                      <span className='text-sm text-muted-foreground'>of</span>
                      <span className='text-sm font-medium text-foreground'>{totalPages || 1}</span>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={handleNextPage}
                      disabled={page >= totalPages || loading || !hasMore}
                    >
                      Next
                      <ChevronRight className='h-4 w-4 ml-1' />
                    </Button>
                  </div>
                </div>
              )}

              {/* Loading indicator for pagination */}
              {loadingMore && (
                <div className='flex items-center justify-center py-4'>
                  <Loader2 className='h-4 w-4 animate-spin text-muted-foreground mr-2' />
                  <span className='text-sm text-muted-foreground'>Loading zones...</span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{editingZone ? 'Edit Zone' : 'Create New Zone'}</DialogTitle>
            <DialogDescription>
              {editingZone
                ? 'Update zone configuration and zip code mappings'
                : 'Create a new zone and map zip codes'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='zone_name'>
                  Zone Name <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='zone_name'
                  value={formData.zone_name}
                  onChange={e => setFormData(prev => ({ ...prev, zone_name: e.target.value }))}
                  placeholder='e.g., US East'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='zone_slug'>Slug</Label>
                <Input
                  id='zone_slug'
                  value={formData.zone_slug}
                  onChange={e => setFormData(prev => ({ ...prev, zone_slug: e.target.value }))}
                  placeholder='e.g., us-east'
                />
              </div>
            </div>

            <div className='grid grid-cols-4 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='oem'>
                  OEM <span className='text-destructive'>*</span>
                </Label>
                <Select
                  value={formData.oem}
                  onValueChange={value => setFormData(prev => ({ ...prev, oem: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select OEM' />
                  </SelectTrigger>
                  <SelectContent>
                    {KNOWN_OEMS.filter(oem => oem !== 'Other').map(oem => (
                      <SelectItem key={oem} value={oem}>
                        {oem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='region'>Region</Label>
                <Input
                  id='region'
                  value={formData.region}
                  onChange={e => setFormData(prev => ({ ...prev, region: e.target.value }))}
                  placeholder='e.g., Northeast'
                />
                <p className='text-xs text-muted-foreground'>
                  Multiple zones can share the same region
                </p>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='country'>Country</Label>
                <Input
                  id='country'
                  value={formData.country}
                  onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder='e.g., US'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='status'>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={value => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='active'>Active</SelectItem>
                    <SelectItem value='inactive'>Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder='Zone description...'
                rows={2}
              />
            </div>

            <div className='space-y-2 border-t pt-4'>
              <div className='flex items-center justify-between mb-2'>
                <Label>Zip Codes</Label>
                <div className='text-sm text-muted-foreground'>
                  {formData.zip_codes.length} zip code{formData.zip_codes.length !== 1 ? 's' : ''}{' '}
                  mapped
                </div>
              </div>

              {/* Preview Recall Count */}
              {formData.oem && formData.zip_codes.length > 0 && (
                <div className='flex items-center gap-2 p-3 bg-muted/50 rounded-md mb-4'>
                  <Info className='h-4 w-4 text-muted-foreground flex-shrink-0' />
                  <div className='text-sm'>
                    <span className='text-muted-foreground'>
                      Estimated recalls for {formData.oem}:{' '}
                    </span>
                    {previewLoading ? (
                      <span className='inline-flex items-center gap-1'>
                        <Loader2 className='h-3 w-3 animate-spin' />
                        Calculating...
                      </span>
                    ) : previewRecallCount !== null ? (
                      <span className='font-semibold text-foreground'>
                        {previewRecallCount.toLocaleString()}
                      </span>
                    ) : (
                      <span className='text-muted-foreground'>—</span>
                    )}
                  </div>
                </div>
              )}

              {/* Add Zip Code Input */}
              <div className='flex gap-2 mb-4'>
                <Input
                  placeholder='Enter zip code (e.g., 33612)'
                  value={newZipCode}
                  onChange={e => setNewZipCode(e.target.value)}
                  onKeyPress={handleZipCodeKeyPress}
                  className='flex-1'
                />
                <Button
                  type='button'
                  onClick={addZipCode}
                  disabled={!newZipCode.trim() || formData.zip_codes.includes(newZipCode.trim())}
                >
                  <Plus className='h-4 w-4 mr-2' />
                  Add
                </Button>
              </div>

              {/* List of Mapped Zip Codes */}
              {formData.zip_codes.length === 0 ? (
                <div className='border rounded-md p-8 text-center'>
                  <p className='text-sm text-muted-foreground'>
                    No zip codes mapped yet. Add zip codes using the input above.
                  </p>
                </div>
              ) : (
                <div className='border rounded-md p-4'>
                  <div className='flex flex-wrap gap-2'>
                    {formData.zip_codes.map(zip => (
                      <Badge
                        key={zip}
                        variant='secondary'
                        className='text-sm py-1 px-3 flex items-center gap-2'
                      >
                        <Hash className='h-3 w-3' />
                        {zip}
                        <button
                          onClick={() => removeZipCode(zip)}
                          className='ml-1 hover:text-destructive rounded-full p-0.5'
                          type='button'
                          aria-label={`Remove ${zip}`}
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <div className='flex items-center justify-between w-full'>
              {zipCodeProgress && (
                <div className='flex items-center text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  {zipCodeProgress}
                </div>
              )}
              <div className='flex gap-2 ml-auto'>
                <Button
                  variant='outline'
                  onClick={() => setDialogOpen(false)}
                  disabled={formLoading}
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={formLoading}>
                  {formLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
                  {editingZone ? 'Update' : 'Create'} Zone
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className='sm:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5 text-destructive' />
              Delete Zone
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this zone? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {zoneToDelete && (
            <div className='py-4'>
              <div className='rounded-lg border bg-muted/30 p-4'>
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-muted-foreground'>Zone Name</span>
                    <span className='font-semibold'>{zoneToDelete.zone_name}</span>
                  </div>

                  {zoneToDelete.region && (
                    <div className='flex items-center justify-between'>
                      <span className='text-sm font-medium text-muted-foreground'>Region</span>
                      <span>{zoneToDelete.region}</span>
                    </div>
                  )}

                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-muted-foreground'>ZIP Codes</span>
                    <Badge variant='outline'>
                      <Hash className='h-3 w-3 mr-1' />
                      {zoneToDelete.zip_code_count || 0}
                    </Badge>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-muted-foreground'>Status</span>
                    <Badge variant={zoneToDelete.status === 'active' ? 'default' : 'secondary'}>
                      {zoneToDelete.status === 'active' ? (
                        <>
                          <CheckCircle className='h-3 w-3 mr-1' />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className='h-3 w-3 mr-1' />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className='mt-4 p-3 bg-destructive/5 border border-destructive/20 rounded-lg'>
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='w-4 h-4 text-destructive mt-0.5 flex-shrink-0' />
                  <div className='text-sm text-destructive'>
                    <p className='font-medium mb-1'>Warning: This is permanent</p>
                    <p>
                      This will permanently delete the zone and all its ZIP code associations. Any
                      references to this zone will be removed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant='outline' onClick={cancelDelete} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={confirmDelete}
              disabled={deleting}
              className='min-w-24'
            >
              {deleting ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className='w-4 h-4 mr-2' />
                  Delete Zone
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ZoneSetupTab: React.FC = () => {
  return (
    <div className='flex w-full flex-col px-4 pb-6 pt-2'>
      <ManualZoneTab />
    </div>
  );
};
